import { z } from "zod";
import { ZDateish, ZEmailLowercase, // ✅ add this to foundationSchema (or define locally)
ZCompatibleMultiplacerModuleKey, ZProjectNo, } from "./foundationSchema";
/* -------------------------------------------------------------------------- */
/*  Modules enum (from MULTIPLACER_MODULES)                                   */
/* -------------------------------------------------------------------------- */
export const ZModulePermission = z.object({
    module: ZCompatibleMultiplacerModuleKey,
});
/* -------------------------------------------------------------------------- */
/*  Project permissions                                                       */
/* -------------------------------------------------------------------------- */
export const ZProjectPermissions = z.object({
    modules: z.array(ZModulePermission).default([]),
    updatedAt: ZDateish.optional(),
});
/* -------------------------------------------------------------------------- */
/*  Project invites (embedded in project doc)                                 */
/* -------------------------------------------------------------------------- */
export const ZProjectInviteStatus = z.enum(["PENDING", "ACCEPTED", "REVOKED"]);
/**
 * IMPORTANT:
 * - Keep output schemas explicit for OpenAPI. Legacy module preprocessing wraps
 *   the canonical enum so generated clients still expose only current module keys.
 * - Use ZEmailLowercase here (no transform), and normalize to lowercase in code when creating.
 */
export const ZProjectInvite = z.object({
    _id: z.any().optional(),
    email: ZEmailLowercase, // ✅ was ZEmailInput (transform) -> causes zod-openapi crash
    permissions: ZProjectPermissions.default({ modules: [] }),
    invitedBy: z.any(),
    createdAt: ZDateish,
    expiresAt: ZDateish.optional(),
    acceptedAt: ZDateish.optional(),
    acceptedBy: z.any().optional(),
    status: ZProjectInviteStatus.default("PENDING"),
});
/* -------------------------------------------------------------------------- */
/*  Project members                                                          */
/* -------------------------------------------------------------------------- */
export const ZProjectMemberRole = z.enum(["OWNER", "ADMIN", "MEMBER", "VIEWER"]);
export const ZProjectMember = z.object({
    userId: z.any(),
    displayName: z.string().optional().default(""),
    email: z.string().optional(),
    role: ZProjectMemberRole.default("MEMBER"),
    permissions: ZProjectPermissions.optional(),
    addedAt: ZDateish.optional(),
    addedBy: z.any().optional(),
});
/* -------------------------------------------------------------------------- */
/*  Base project                                                              */
/* -------------------------------------------------------------------------- */
export const ZFileAttrField = z.enum(["fileAttr1", "fileAttr2", "fileAttr3", "fileAttr4"]);
export const ZEnrichStatus = z.object({
    status: z.enum(["idle", "running", "done"]),
    total: z.number().int().min(0),
    done: z.number().int().min(0),
    failed: z.number().int().min(0).optional(),
    startedAt: ZDateish.optional().nullable(),
    finishedAt: ZDateish.optional().nullable(),
});
export const ZAirTerminalDirection = z.enum(["SUPPLY", "EXHAUST", "RETURN", "TRANSFER"]);
export const ZCompCodeDirectionRule = z.object({
    prefix: z.string().min(1),
    direction: ZAirTerminalDirection,
    description: z.string().optional(),
});
export const ZVentilationSettings = z.object({
    compCodeDirectionRules: z.array(ZCompCodeDirectionRule).default([]),
    updatedAt: ZDateish.optional(),
});
export const ZDrofusInterfaceScheduleDay = z.enum([
    "Sun",
    "Mon",
    "Tue",
    "Wed",
    "Thu",
    "Fri",
    "Sat",
]);
export const ZDrofusInterfaceOccurrencesSyncSchedule = z.object({
    enabled: z.boolean().default(false),
    days: z.array(ZDrofusInterfaceScheduleDay).min(1).default(["Sun", "Mon", "Tue", "Wed", "Thu"]),
    hour: z.number().int().min(0).max(23).default(2),
    minute: z.number().int().min(0).max(59).default(0),
    timezone: z.string().min(1).default("W. Europe Standard Time"),
    cronExpression: z.string().min(1).default("0 0 2 * * Sun-Thu"),
    pageSize: z.number().int().min(100).max(2000).default(1000),
    maxRows: z.number().int().positive().max(100000).nullable().default(null),
    updatedAt: ZDateish.optional(),
    lastTriggeredAt: ZDateish.optional(),
    lastTriggeredSlot: z.string().min(1).optional(),
});
const ZArticlePropertyKey = z.string().regex(/^[a-z][a-zA-Z0-9_]*$/);
/** Source-independent coloring of model objects, matched by article number/compCode. */
export const ZArticleColoring = z.object({
    id: ZArticlePropertyKey,
    label: z.string().min(1),
    propertyKey: ZArticlePropertyKey,
    // Optional override for missing articles/properties and unmapped values.
    fallbackColor: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
    values: z.array(z.object({
        value: z.string().min(1),
        label: z.string().min(1).optional(),
        color: z.string().regex(/^#[0-9a-fA-F]{6}$/),
    })),
});
/** Maps an article field fetched through dRofus /occurrences to a stable property key. */
export const ZDrofusArticleProperty = z.object({
    key: ZArticlePropertyKey,
    occurrenceField: z.string().regex(/^article_id_[a-zA-Z0-9_]+$/),
});
export const ZDrofusInterfaceProjectSettings = z.object({
    occurrencesSyncSchedule: ZDrofusInterfaceOccurrencesSyncSchedule.optional(),
    articleProperties: z.array(ZDrofusArticleProperty).optional(),
});
export const ZArticleValidationExclusion = z.object({
    compCode: z.string().min(1),
    description: z.string().min(1).regex(/\S/, "Description must not be blank"),
    addedBy: z.object({
        userId: z.string().min(1),
        displayName: z.string().min(1),
    }),
    addedAt: z.string().datetime(),
});
export const ZArticleValidationSettings = z.object({
    /** One exclusion per exact, case-sensitive compCode. */
    ignoredCompCodes: z.array(ZArticleValidationExclusion),
});
/** Optional jobs in room sync; MCBIM rooms and snapshots always run. */
export const ZRoomSyncSettings = z.object({
    drofusRooms: z.boolean().default(false),
    drofusSpecifiedArticles: z.boolean().default(false),
    deriveRoomExtraIfcInfo: z.boolean().default(true),
    validateSpecifiedArticles: z.boolean().default(false),
    standardRoomValidationDisciplines: z
        .array(z.string().trim().min(1))
        .default([]),
    mlPredictCeilingDevices: z.boolean().default(false),
});
export const ZRoomSyncToggleKey = ZRoomSyncSettings.omit({
    standardRoomValidationDisciplines: true,
}).keyof();
export const ZProjectBase = z.object({
    _id: z.any().optional(),
    projectNo: ZProjectNo,
    title: z.string().min(1),
    compCodePath: z.string(),
    cadCompCodePath: z.string().optional(),
    pnsPath: z.string().optional(),
    areaPath: z.string().optional(),
    mmiPath: z.string().optional(),
    disciplineFileAttr: ZFileAttrField.optional(),
    enrich: ZEnrichStatus.optional(),
    createdAt: ZDateish.optional(),
    updatedAt: ZDateish.optional(),
    ventilation: ZVentilationSettings.optional(),
    /** Missing settings mean no project-level exclusions. */
    articleValidation: ZArticleValidationSettings.optional(),
    articleColorings: z.array(ZArticleColoring).optional(),
    drofusInterface: ZDrofusInterfaceProjectSettings.optional(),
    roomSync: ZRoomSyncSettings.optional(),
});
/* -------------------------------------------------------------------------- */
/*  Drofus                                                                    */
/* -------------------------------------------------------------------------- */
export const ZDrofusSettingsPublic = z.object({
    drofusBaseUrl: z.string().url(),
    drofusDb: z.string(),
    drofusProject: z.string(),
    hasApiKey: z.boolean(),
    last4: z.string().min(1).max(4).optional(),
    verifiedAt: ZDateish.optional(),
    updatedAt: ZDateish.optional(),
});
export const ZEncryptedSecret = z.object({
    c: z.string(),
    iv: z.string(),
    tag: z.string(),
    last4: z.string().min(1).max(4),
    kid: z.string().optional(),
    alg: z.literal("AES-256-GCM").optional(),
    verifiedAt: ZDateish.optional(),
    updatedAt: ZDateish.optional(),
});
export const ZDrofusSettings = z.object({
    drofusBaseUrl: z.string().url(),
    drofusDb: z.string(),
    drofusProject: z.string(),
    apiKeyEnc: ZEncryptedSecret.optional(),
});
